import supabase from '../config/supabaseConfig';

const SUBSCRIPTION_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

export type SubscriptionWithPlan = {
  id: string;
  user_id: string;
  user_email: string;
  plan_id: string;
  plan_name: string;
  plan_max_uploads: number;
  start_date: string;
  end_date: string;
  status: string;
  upload_count: number;
  last_renewal_date: string | null;
  next_renewal_date: string | null;
  created_at: string;
  updated_at: string;
  plans: {
    name: string;
    max_uploads: number;
    auto_renew_hours: number | null;
    features: string[];
    price: number;
    interval: string;
  } | null;
};

/** Returns the user's current active, non-expired subscription (newest first). */
export async function getActiveSubscription(userId: string) {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      *,
      plans (
        name,
        max_uploads,
        auto_renew_hours,
        features,
        price,
        interval
      )
    `)
    .eq('user_id', userId)
    .eq('status', 'active')
    .gt('end_date', now)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return { data: data as SubscriptionWithPlan | null, error };
}

/** Returns the user's newest active subscription, even when its end date has passed. */
export async function getLatestActiveSubscription(userId: string) {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      *,
      plans (
        name,
        max_uploads,
        auto_renew_hours,
        features,
        price,
        interval
      )
    `)
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return { data: data as SubscriptionWithPlan | null, error };
}

export function getNextRenewalDate(fromDate: Date, autoRenewHours?: number | null) {
  if (!autoRenewHours) {
    return new Date(fromDate.getTime() + SUBSCRIPTION_DURATION_MS);
  }

  return new Date(fromDate.getTime() + autoRenewHours * 60 * 60 * 1000);
}

// Renewal writes (status, dates, plan snapshot) used to go through a direct
// .update() here, gated only by "is this row mine?" -- which is also all
// the subscriptions RLS policy checked. That meant any authenticated user
// could skip this function entirely and PATCH their own row via a raw
// REST call with status: 'active' and any plan_id they liked, no payment
// or admin approval required. The fix moves this write into the
// renew_my_subscription() SECURITY DEFINER RPC, which re-derives the plan,
// re-checks that it's actually eligible for auto-renewal, and re-checks
// that the subscription has actually expired -- all server-side, where a
// client can't skip them.
export async function renewSubscription(subscription: SubscriptionWithPlan) {
  const autoRenewHours = subscription.plans?.auto_renew_hours;

  if (!autoRenewHours) {
    return subscription;
  }

  const { data, error } = await supabase
    .rpc('renew_my_subscription', { p_subscription_id: subscription.id });

  if (error) throw error;
  // Renewal never changes which plan the subscription points to, only its
  // status/dates/count -- safe to reuse the plan object already on hand
  // rather than re-querying it.
  return { ...(data as SubscriptionWithPlan), plans: subscription.plans };
}

/**
 * Ensures exactly one active subscription exists for the user.
 * Idempotent — safe to call from auth callback, sell page, etc.
 * Returns the active subscription without creating duplicates.
 *
 * The actual plan lookup and insert now happen inside the
 * bootstrap_free_subscription() SECURITY DEFINER RPC, which hardcodes the
 * "Gratuit" plan server-side. A direct client-side insert with a
 * self-scoped RLS check (user_id = auth.uid()) would have let a client
 * insert any plan_id/status it wanted, not just the free one.
 */
// userEmail is unused now that bootstrap_free_subscription() derives the
// email server-side from auth.users -- kept in the signature so every
// existing call site (auth callback, ad creation) doesn't need to change.
export async function ensureFreeSubscription(userId: string, _userEmail: string) {
  const { data: existing, error: fetchError } = await getActiveSubscription(userId);
  if (fetchError) throw fetchError;
  if (existing) {
    return { subscription: existing, created: false };
  }

  const { error } = await supabase.rpc('bootstrap_free_subscription');
  if (error) throw error;

  // Re-fetch through the normal joined query rather than trusting the
  // RPC's bare-row return shape for the nested `plans` embed.
  const { data: created, error: refetchError } = await getActiveSubscription(userId);
  if (refetchError) throw refetchError;
  if (!created) throw new Error('Subscription bootstrap succeeded but row was not found on refetch');

  return { subscription: created, created: true };
}

/** Cancels extra active subscriptions, keeping only the given one. */
export async function cancelOtherActiveSubscriptions(userId: string, keepSubscriptionId: string) {
  const { error } = await supabase
    .from('subscriptions')
    .update({
      status: 'cancelled',
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .eq('status', 'active')
    .neq('id', keepSubscriptionId);

  if (error) throw error;
}
